import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userAuthService } from '../services/userAuthService';
import { API_BASE_URL } from '../config/supabase';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { handlePhoneInput } from '../utils/inputValidation';

const SSOLoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [otpResponse, setOtpResponse] = useState<any>(null);

  const handleGenerateOTP = async () => {
    if (!mobileNumber) {
      setError(String(t('auth.pleaseEnterMobile')));
      return;
    }

    if (!userAuthService.validateMobileNumber(mobileNumber)) {
      setError(String(t('auth.pleaseEnterValidMobile')));
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await userAuthService.generateOTP({ mobile_number: mobileNumber });
      
      if (response.success) {
        setMessage(t('auth.otpSentSuccessfully') as string);
        setOtpResponse({ otp: response.otp });
        setStep('otp');
      } else {
        setError(response.error || t('auth.failedToSendOtp') as string);
      }
    } catch (err: any) {
      setError(err.message || t('auth.failedToGenerateOtpTryAgain') as string);
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    if (!otp) {
      setError(t('auth.pleaseEnterOtp') as string);
      return;
    }

    if (otp.length !== 6) {
      setError(t('auth.enterOtp') as string);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call SSO login endpoint
      const response = await fetch(`${API_BASE_URL}/user-auth/sso/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mobile_number: mobileNumber,
          otp: otp
        }),
      });

      if (response.ok) {
        // Get HTML response and display it in a new window
        const html = await response.text();
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(html);
          newWindow.document.close();
          setMessage(t('pages.ssoLogin.redirecting') as string);
        } else {
          setError(t('pages.ssoLogin.popupBlocked') as string);
        }
      } else {
        const result = await response.json();
        setError(result.error || t('pages.ssoLogin.loginFailed') as string);
      }
    } catch (err: any) {
      setError(err.message || t('pages.ssoLogin.loginFailed') as string);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToMobile = () => {
    setStep('mobile');
    setOtp('');
    setError('');
    setMessage('');
    setOtpResponse(null);
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>

        {/* Back to Main Link */}
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={handleBackToMain}
            className="text-white hover:text-orange-200 transition-colors flex items-center text-sm sm:text-base"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('pages.userLogin.backToMain')}
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{t('pages.ssoLogin.title')}</h1>
            <p className="text-white/90 text-xs sm:text-sm mt-1">{t('pages.ssoLogin.subtitle')}</p>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {step === 'mobile' ? (
              <div className="space-y-4 sm:space-y-6">
                {/* Form Title */}
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">{t('pages.ssoLogin.staffSSO')}</h2>
                  <p className="text-sm sm:text-base text-gray-600">{t('pages.ssoLogin.staffSSODesc')}</p>
                </div>

                {/* Mobile Number Input */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-2">
                    {t('auth.staffMobileNumber')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-xs sm:text-sm font-medium">+91</span>
                    </div>
                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(handlePhoneInput(e))}
                      placeholder={t('auth.enterMobileNumber') as string}
                      maxLength={10}
                      className="w-full pl-12 sm:pl-14 pr-10 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-sm sm:text-base"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-xs sm:text-sm">📱</span>
                    </div>
                  </div>
                </div>

                {/* Authentication Button */}
                <button
                  onClick={handleGenerateOTP}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm sm:text-base"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {loading ? t('auth.sendingOtp') : t('auth.authenticateStaff')}
                </button>

                {/* Secure Authentication Text */}
                <div className="flex items-center justify-center text-center pt-2 sm:pt-4">
                  <svg className="w-4 h-4 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  <span className="text-gray-600 text-xs sm:text-sm">{t('auth.secureAuthText')}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {/* Form Title */}
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">{t('auth.otpVerification')}</h2>
                  <p className="text-sm sm:text-base text-gray-600">{t('auth.otpVerificationDesc')}</p>
                </div>

                {/* OTP Display - Only show if OTP is available */}
                {otpResponse?.otp && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 text-center">
                    <h3 className="text-blue-800 font-bold mb-2 text-sm sm:text-base flex items-center justify-center">
                      <span className="mr-2">📱</span>
                      {t('auth.verificationCodeLabel')}
                    </h3>
                    <p className="text-blue-700 text-xs sm:text-sm mb-2">{t('auth.yourOtpIs')}</p>
                    <div className="bg-blue-100 rounded-lg p-2 sm:p-3">
                      <span className="text-blue-800 text-2xl sm:text-3xl font-bold tracking-widest">
                        {otpResponse.otp}
                      </span>
                    </div>
                    <p className="text-blue-700 text-xs mt-2">{t('auth.enterThisCode')}</p>
                  </div>
                )}
                
                {/* OTP Input */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-2">
                    {t('auth.verificationCode')}
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder={t('auth.enterOtp') as string}
                    maxLength={6}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-center text-lg sm:text-2xl tracking-widest text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                  />
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 text-center">
                    {t('auth.otpSentTo', { mobile: mobileNumber })}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2 sm:space-y-3">
                  <button
                    onClick={handleSSOLogin}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm sm:text-base"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                    ) : null}
                    {loading ? t('pages.ssoLogin.redirecting') : t('pages.ssoLogin.loginWithSSO')}
                  </button>
                  
                  <button
                    onClick={handleBackToMobile}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors text-sm sm:text-base"
                  >
                    {t('auth.back')}
                  </button>
                </div>

                {/* OTP Help */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <h3 className="text-blue-800 font-semibold mb-2 text-sm sm:text-base flex items-center">
                    <span className="mr-2">💡</span>
                    {t('auth.otpHelp')}
                  </h3>
                  <div className="text-xs sm:text-sm text-blue-700 space-y-1">
                    <p>• {t('auth.otpValidFor')}</p>
                    <p>• {t('auth.maxAttempts')}</p>
                    <p>• {t('auth.forTestingOtpShown')}</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center">
                  <span className="text-red-600 text-base sm:text-lg mr-2">⚠️</span>
                  <p className="text-red-800 text-xs sm:text-sm font-medium">{error}</p>
                </div>
              </div>
            )}

            {message && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center">
                  <span className="text-green-600 text-base sm:text-lg mr-2">✅</span>
                  <p className="text-green-800 text-xs sm:text-sm font-medium">{message}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AUGMONT Logo */}
        <div className="text-center mt-4 sm:mt-6 md:mt-8">
          <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
            <p className="text-gray-600 text-xs sm:text-sm mb-1">{t('common.poweredBy')}</p>
            <img 
              src="/images/Asset1.png" 
              alt={t('common.poweredBy') as string} 
              className="h-4 sm:h-5 md:h-6 w-auto mx-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSOLoginPage;

